import type {
  DataLayerProperties,
  Ga4Item,
  ListIdentity,
  OrderItem,
  Product,
  ShippingInfoProduct,
} from '@/types';

/**
 * GA4's five category levels, omitting any the product does not have.
 *
 * #2894 - these were assigned unconditionally, so a three-level product still emitted
 * `item_category4: undefined` and `item_category5: undefined`. A key set to undefined is
 * still a key on the object pushed to dataLayer, so absent levels have to be left out
 * entirely rather than set to nothing.
 */
const categoryLevels = (categories: string[] | null | undefined) => {
  const keys = [
    'item_category',
    'item_category2',
    'item_category3',
    'item_category4',
    'item_category5',
  ] as const;

  return Object.fromEntries(
    (categories ?? [])
      .slice(0, keys.length)
      .map((value, i) => [keys[i], value] as const)
      .filter(([, value]) => value != null && value !== ''),
  );
};

const imageUrl = ({ image }: Pick<Product, 'image'>): string | undefined =>
  image?.mainSrc ?? image?.thumbnailSrc ?? undefined;

/**
 * Map a hydration `Product` to a GA4 item. `list`/`index` are set only for
 * list contexts (view_item_list / select_item); omit both for view_item (PDP).
 * `category` is spread into GA4's five category levels.
 */
export const toGa4Item = (
  product: Product,
  list?: ListIdentity,
  index?: number,
): Ga4Item => {
  const cats = product.category ?? [];
  const item: Ga4Item = {
    item_id: product.easonsProductId ?? product.sku ?? '',
    item_name: product.name ?? '',
    item_brand: product.author ?? undefined,
    item_variant: product.variant ?? undefined,
    price: product.price?.amount,
    ...categoryLevels(cats),
    image_url: imageUrl(product),
  };
  if (list !== undefined) {
    item.item_list_name = list.name;
    if (list.id !== undefined) {
      item.item_list_id = list.id;
    }
  }
  if (index !== undefined) {
    item.index = index;
  }
  return item;
};

/**
 * Map a cart `OrderItem` to a GA4 item. Single source of truth for cart/checkout
 * events (addToCart, removeFromCart, addPaymentInfo, addShippingInfo) —
 * replaces the old per-component DataLayerProperties re-mapping.
 */
export const orderItemToGa4Item = (item: OrderItem): Ga4Item => {
  const cats = item.category ?? [];
  return {
    item_id: item.easonsProductId ?? item.sku ?? '',
    item_name: item.name ?? '',
    item_brand: item.brand ?? item.author ?? undefined,
    item_variant: item.variant ?? undefined,
    ...categoryLevels(cats),
    price: item.price?.amount,
    quantity: item.quantity,
    image_url: imageUrl(item),
  };
};

/**
 * Map a cart `OrderItem` to the legacy UA Enhanced Ecommerce product shape the
 * GTM container was built against (pre-GA4, commit 08b08753). Deliberately
 * mirrors the old mapping exactly — formatted `price.current` string, no
 * `author` fallback on brand — so existing Data Layer Variables keep resolving.
 */
export const orderItemToLegacyProduct = (
  item: OrderItem,
  quantity?: number,
  priceOverride?: number,
): DataLayerProperties => ({
  id: item.easonsProductId || '',
  name: item.name || '',
  brand: item.brand || '',
  category: item.category || [],
  variant: item.variant || '',
  price: priceOverride ?? item.price?.amount ?? 0,
  quantity: quantity ?? item.quantity,
  image_url: imageUrl(item),
});

/**
 * Map a cart `OrderItem` to the product shape used by `addShippingInfo` ONLY:
 * the legacy keys minus `category`, whose levels are emitted as the separate
 * `item_category`…`item_category5` properties instead. A level absent from the
 * array is omitted. Every other legacy event keeps the `category` array via
 * `orderItemToLegacyProduct`.
 */
export const orderItemToShippingInfoProduct = (
  item: OrderItem,
): ShippingInfoProduct => {
  const cats = item.category ?? [];
  const { category: _category, ...product } = orderItemToLegacyProduct(item);
  return {
    ...product,
    ...categoryLevels(cats),
  };
};
